import { useState } from 'react'
import { ChevronDown, ChevronUp, Check, X, Pencil, Save, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { TaxonomyIntent } from '@/hooks/useTaxonomyRun'

interface IntentCardProps {
  intent: TaxonomyIntent
  onApprove:       (intentId: string) => void
  onReject:        (intentId: string) => void
  onEdit:          (intentId: string, name: string, description: string) => void
  onApproveTag:    (tagId: string) => void
  onRejectTag:     (tagId: string) => void
  onApproveEntity: (entityId: string) => void
  onRejectEntity:  (entityId: string) => void
}

// Status dot colour map
const STATUS_DOT: Record<string, string> = {
  pending:  'bg-amber-400',
  approved: 'bg-green-500',
  rejected: 'bg-red-500',
}

export function IntentCard({
  intent, onApprove, onReject, onEdit,
  onApproveTag, onRejectTag, onApproveEntity, onRejectEntity,
}: IntentCardProps) {
  const [expanded, setExpanded] = useState(false)
  const [editing,  setEditing]  = useState(false)

  // Edit form local state — initialised from intent
  const [editName, setEditName]             = useState(intent.name)
  const [editDescription, setEditDescription] = useState(intent.description)

  // Reset edit fields when intent prop changes (e.g. after save)
  // (simple approach — works because intent.id is stable)

  const handleSave = () => {
    onEdit(intent.id, editName, editDescription)
    setEditing(false)
  }
  const handleCancel = () => {
    setEditName(intent.name)
    setEditDescription(intent.description)
    setEditing(false)
  }

  // State machine guard: buttons disabled once approved or rejected
  const isTerminal = intent.approved !== 'pending'

  return (
    <div className="border rounded-lg bg-white shadow-sm overflow-hidden">

      {/* ── Header row (always visible, clickable to toggle) ── */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 min-w-0">
          {/* Status dot */}
          <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', STATUS_DOT[intent.approved])} />

          {/* Name */}
          <span className="font-medium text-gray-900 truncate">
            {intent.name}
          </span>

          {/* Doc count badge */}
          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full shrink-0">
            {intent.document_count} docs
          </span>
        </div>

        {/* Chevron */}
        {expanded
          ? <ChevronUp   className="w-4 h-4 text-gray-400 shrink-0" />
          : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
        }
      </button>

      {/* ── Body (only when expanded) ── */}
      {expanded && (
        <div className="border-t px-4 py-3 space-y-4">

          {/* Description — static or editable */}
          {editing ? (
            <div className="space-y-2">
              <div>
                <label className="text-xs text-gray-500 font-medium">Name</label>
                <Input
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  className="h-8 text-sm mt-0.5"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium">Description</label>
                <Input
                  value={editDescription}
                  onChange={e => setEditDescription(e.target.value)}
                  className="h-8 text-sm mt-0.5"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" variant="default" onClick={handleSave} className="gap-1">
                  <Save className="w-3.5 h-3.5" /> Save
                </Button>
                <Button size="sm" variant="ghost" onClick={handleCancel} className="gap-1">
                  <Undo2 className="w-3.5 h-3.5" /> Cancel
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-600">{intent.description}</p>
          )}

          {/* ── Tags section ── */}
          {intent.tags.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Tags</p>
              <div className="flex flex-wrap gap-2">
                {intent.tags.map(tag => {
                  const tagTerminal = tag.approved !== 'pending'
                  return (
                    <div
                      key={tag.id}
                      className={cn(
                        'inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm',
                        tag.approved === 'approved' && 'bg-green-50 border-green-200',
                        tag.approved === 'rejected' && 'bg-red-50 border-red-200 opacity-60',
                        tag.approved === 'pending'  && 'bg-gray-50 border-gray-200',
                      )}
                    >
                      <span className="font-medium text-gray-800">{tag.name}</span>
                      <span className="text-gray-500">{tag.description}</span>

                      {/* Per-tag approve / reject (tiny buttons) */}
                      {!tagTerminal && (
                        <span className="flex gap-0.5 ml-1">
                          <button onClick={(e) => { e.stopPropagation(); onApproveTag(tag.id) }}  className="p-0.5 rounded hover:bg-green-100 text-green-600"><Check className="w-3 h-3" /></button>
                          <button onClick={(e) => { e.stopPropagation(); onRejectTag(tag.id) }}   className="p-0.5 rounded hover:bg-red-100   text-red-500">  <X     className="w-3 h-3" /></button>
                        </span>
                      )}
                      {tag.approved === 'approved' && <Check className="w-3 h-3 text-green-600" />}
                      {tag.approved === 'rejected' && <X     className="w-3 h-3 text-red-500"   />}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* ── Entity Types section ── */}
          {intent.entity_types.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Entity Types</p>
              <div className="space-y-1.5">
                {intent.entity_types.map(et => {
                  const etTerminal = et.approved !== 'pending'
                  return (
                    <div
                      key={et.id}
                      className={cn(
                        'flex items-center gap-3 px-3 py-1.5 rounded-md border text-sm',
                        et.approved === 'approved' && 'bg-green-50 border-green-200',
                        et.approved === 'rejected' && 'bg-red-50 border-red-200 opacity-60',
                        et.approved === 'pending'  && 'bg-gray-50 border-gray-200',
                      )}
                    >
                      {/* Monospace name badge */}
                      <span className="font-mono text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200 shrink-0">
                        {et.name}
                      </span>
                      {/* Description */}
                      <span className="text-gray-600 flex-1 truncate">{et.description}</span>
                      {/* Example */}
                      <span className="text-gray-400 text-xs shrink-0">e.g. {et.example}</span>

                      {/* Per-entity approve / reject */}
                      {!etTerminal && (
                        <span className="flex gap-0.5 ml-1">
                          <button onClick={(e) => { e.stopPropagation(); onApproveEntity(et.id) }} className="p-0.5 rounded hover:bg-green-100 text-green-600"><Check className="w-3 h-3" /></button>
                          <button onClick={(e) => { e.stopPropagation(); onRejectEntity(et.id) }} className="p-0.5 rounded hover:bg-red-100   text-red-500">  <X     className="w-3 h-3" /></button>
                        </span>
                      )}
                      {et.approved === 'approved' && <Check className="w-3 h-3 text-green-600" />}
                      {et.approved === 'rejected' && <X     className="w-3 h-3 text-red-500"   />}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* ── Intent-level action row ── */}
          <div className="flex items-center gap-2 pt-2 border-t">
            {!isTerminal && (
              <>
                <Button size="sm" variant="default" onClick={() => onApprove(intent.id)} className="gap-1 bg-green-600 hover:bg-green-700">
                  <Check className="w-3.5 h-3.5" /> Approve
                </Button>
                {!editing && (
                  <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="gap-1">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => onReject(intent.id)} className="gap-1 text-red-600 border-red-200 hover:bg-red-50">
                  <X className="w-3.5 h-3.5" /> Reject
                </Button>
              </>
            )}
            {/* Terminal state badges */}
            {intent.approved === 'approved' && (
              <span className="flex items-center gap-1 text-sm text-green-700 font-medium">
                <Check className="w-4 h-4" /> Approved
              </span>
            )}
            {intent.approved === 'rejected' && (
              <span className="flex items-center gap-1 text-sm text-red-600 font-medium">
                <X className="w-4 h-4" /> Rejected
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
